import { FARM_TO_FARM_AGGREGATE_KEY, FarmToFarmPeriodState, FarmToFarmReport, FarmToFarmRow } from '@/types';

/**
 * One column of the benchmark for one period: the rollup column, then one per farm.
 *
 * Shared by the on-screen table and the exported workbook. Both render the same concept, and
 * building it twice let the two drift - and made the export rediscover each farm by scanning
 * report.farms once per cell.
 */
export interface FarmToFarmPeriodColumn {
    key: string;
    name: string;
    isAggregate: boolean;
    state: FarmToFarmPeriodState | null;
    note: string | null;
    /** This row's figure in this column, or null where there is none. */
    valueFor(row: FarmToFarmRow): number | null;
}

/**
 * Reads a row's figure out of the report's nested period-then-column string maps, so no caller has
 * to know that shape.
 */
function valueReader(period: string, key: string) {
    return (row: FarmToFarmRow): number | null => row.values?.[period]?.[key] ?? null;
}

/**
 * The column model for one period: the rollup first, then one column per farm in report order.
 *
 * `aggregateLabel` heads the rollup column - the active directory's name - which the report does
 * not carry itself.
 */
export function farmToFarmColumns(report: FarmToFarmReport, period: string, aggregateLabel: string): FarmToFarmPeriodColumn[] {
    const aggregate: FarmToFarmPeriodColumn = {
        key: FARM_TO_FARM_AGGREGATE_KEY,
        name: aggregateLabel,
        isAggregate: true,
        // The rollup is computed over every selected barn as one herd, so it carries no per-farm
        // state of its own.
        state: null,
        note: null,
        valueFor: valueReader(period, FARM_TO_FARM_AGGREGATE_KEY),
    };

    const farms = report.farms.map((farm) => {
        const key = String(farm.farm_id);

        return {
            key,
            name: farm.name,
            isAggregate: false,
            state: farm.period_states?.[period] ?? null,
            note: farm.period_state_notes?.[period] ?? null,
            valueFor: valueReader(period, key),
        };
    });

    return [aggregate, ...farms];
}
